import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.jsx";
import { themeStore } from "./lib/storage";

import { HomeScreen } from "./screens/HomeScreen";
import { AuthScreen } from "./screens/AuthScreen";
import { BookingFlow } from "./screens/BookingFlow";
import { HistoryScreen } from "./screens/HistoryScreen";
import { TechDashboardV2 } from "./screens/TechDashboardV2";
import { AdminDashboard } from "./screens/AdminDashboard";
import { ProfileScreen } from "./screens/ProfileScreen";
import { GalleryScreen } from "./screens/GalleryScreen";
import { TechProfileScreen } from "./screens/TechProfileScreen";
import { TechniciansPage } from "./screens/TechniciansPage";
import { TechnicianDetail } from "./screens/TechnicianDetail";
import { ForgotPasswordScreen } from "./screens/ForgotPasswordScreen";
import { ResetPasswordScreen } from "./screens/ResetPasswordScreen";
import { VerifyEmailScreen } from "./screens/VerifyEmailScreen";
import { LegalScreen } from "./screens/LegalScreen";
import { ChatScreen } from "./screens/ChatScreen";
import { InvoicesScreen } from "./screens/InvoicesScreen";
import { NotificationsScreen } from "./screens/NotificationsScreen";
import { WalkinsScreen } from "./screens/WalkinsScreen";
import { Nav } from "./components/Nav";
import { CookieBanner } from "./components/CookieBanner";
import { Footer } from "./components/Footer";
import { OnboardingWizard } from "./components/OnboardingWizard";
import { CheckoutReturn } from "./screens/CheckoutReturn";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}

function Shell() {
  const [theme, setTheme] = useState("dark");
  const loc = useLocation();
  const navigate = useNavigate();
  const { session, verification, verificationLoading, loading } = useAuth();

  useEffect(() => {
    const stored = themeStore.load();
    if (stored === "light" || stored === "dark") setTheme(stored);
    else if (typeof window !== "undefined") {
      const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
      setTheme(prefersDark ? "dark" : "light");
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.querySelector("meta[name='theme-color']")?.setAttribute("content", theme === "dark" ? "#0D0810" : "#FDF8F3");
  }, [theme]);

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next); themeStore.save(next);
  }

  // Hide the nav on auth screens — they have their own full-bleed look
  const isAuthRoute = loc.pathname === "/login" || loc.pathname === "/signup";
  const isVerificationRoute = loc.pathname === "/verify-email";
  const canStayUnverified = isAuthRoute || isVerificationRoute || loc.pathname === "/forgot-password" || loc.pathname === "/reset-password" || loc.pathname.startsWith("/legal/");

  useEffect(() => {
    if (loading || verificationLoading || !session || !verification || verification.verified || canStayUnverified) return;
    navigate("/verify-email", { replace: true });
  }, [loading, verificationLoading, session, verification, canStayUnverified, navigate]);

  return (
    <>
      {!isAuthRoute && !isVerificationRoute && <Nav theme={theme} onToggleTheme={toggleTheme} />}
      <Routes>
        <Route path="/" element={<HomeScreen theme={theme} onToggleTheme={toggleTheme} />} />
        <Route path="/login" element={<AuthScreen mode="login" theme={theme} />} />
        <Route path="/signup" element={<AuthScreen mode="signup" theme={theme} />} />
        <Route path="/book" element={<RequireAuth><BookingFlow /></RequireAuth>} />
        <Route path="/history" element={<RequireAuth><HistoryScreen /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><ProfileScreen theme={theme} onToggleTheme={toggleTheme} /></RequireAuth>} />
        <Route path="/tech" element={<RequireRole allowed={["technician","admin_tech"]}><TechDashboardV2 /></RequireRole>} />
        <Route path="/admin" element={<RequireRole allowed={["admin_tech"]}><AdminDashboard /></RequireRole>} />
        <Route path="/gallery" element={<GalleryScreen />} />
        <Route path="/checkout/return" element={<CheckoutReturn />} />
        <Route path="/tech-profile/:id" element={<TechProfileScreen />} />
        <Route path="/technicians" element={<TechniciansPage />} />
        <Route path="/technicians/:id" element={<TechnicianDetail />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/verify-email" element={<VerifyEmailScreen />} />
        <Route path="/legal/:doc" element={<LegalScreen />} />
        <Route path="/chat" element={<RequireAuth><ChatScreen /></RequireAuth>} />
        <Route path="/invoices" element={<RequireAuth><InvoicesScreen /></RequireAuth>} />
        <Route path="/notifications" element={<RequireAuth><NotificationsScreen /></RequireAuth>} />
        <Route path="/walkins" element={<RequireAuth><WalkinsScreen /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isAuthRoute && !isVerificationRoute && <Footer />}
      {!isAuthRoute && !isVerificationRoute && <OnboardingWizard />}
      <CookieBanner />
    </>
  );
}

function RequireAuth({ children }) {
  const { session, loading, verification, verificationLoading } = useAuth();
  if (loading || verificationLoading) return <FullPageMsg>Loading…</FullPageMsg>;
  if (!session) return <Navigate to="/login" replace />;
  if (verification && !verification.verified) return <Navigate to="/verify-email" replace />;
  return children;
}

function RequireRole({ children, allowed }) {
  const { session, profile, loading, verification, verificationLoading } = useAuth();
  if (loading || verificationLoading) return <FullPageMsg>Loading…</FullPageMsg>;
  if (!session) return <Navigate to="/login" replace />;
  if (verification && !verification.verified) return <Navigate to="/verify-email" replace />;
  if (!allowed.includes(profile?.role)) return <FullPageMsg>403 — you don't have access to this area.</FullPageMsg>;
  return children;
}

function FullPageMsg({ children }) {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
      {children}
    </div>
  );
}
